/**
 * Maps shared catalog integration IDs to legacy tenant document `type` values.
 * Legacy integrations were stored as admin/{tenant}/integrations docs with a
 * `type` field and config at the document root (no sharedIntegrationId/status).
 */
export const SHARED_TO_LEGACY_TYPE = {
  'google-tag-manager': 'gtm',
  'google-recaptcha': 'recaptcha',
  'visual-web-optimizer': 'vwo',
  'microsoft-dynamics-365': 'microsoft-dynamics',
  salesforce: 'salesforce',
  'salesforce-marketing-cloud': 'salesforce-marketing-cloud',
  findock: 'findock',
  'google-drive': 'google_drive',
  onedrive: 'onedrive',
  'postcode-nl': 'postcodenl',
}

export const LEGACY_TYPE_TO_SHARED = Object.entries(SHARED_TO_LEGACY_TYPE)
  .reduce((acc, [sharedId, legacyType]) => {
    acc[legacyType] = sharedId
    return acc
  }, {})

/**
 * Whether a tenant integration document represents an active activation
 * of the given shared catalog integration.
 *
 * @param {Object} integration Tenant integration document
 * @param {string} sharedIntegrationId Shared catalog integration ID
 * @returns {boolean}
 */
export const matchesSharedIntegration = (integration, sharedIntegrationId) => {
  if (!integration || !sharedIntegrationId) return false
  if (integration.status === 'inactive') return false

  if (integration.sharedIntegrationId === sharedIntegrationId) {
    return !integration.status || integration.status === 'active'
  }

  const legacyType = SHARED_TO_LEGACY_TYPE[sharedIntegrationId]
  if (legacyType && integration.type === legacyType) {
    return !integration.status || integration.status === 'active'
  }

  return false
}

/**
 * Find the tenant integration doc for a shared catalog ID.
 *
 * @param {Array<Object>} tenantIntegrations
 * @param {string} sharedIntegrationId
 * @returns {Object|undefined}
 */
export const findTenantIntegration = (tenantIntegrations, sharedIntegrationId) => {
  if (!Array.isArray(tenantIntegrations)) return undefined
  return tenantIntegrations.find(integration => matchesSharedIntegration(integration, sharedIntegrationId))
}

/**
 * Normalize configuration for the manage UI.
 * Prefers nested `configuration`, falls back to legacy root fields.
 *
 * @param {Object} integration Tenant integration document
 * @returns {Object}
 */
export const getIntegrationConfiguration = (integration = {}) => {
  if (integration.configuration && typeof integration.configuration === 'object') {
    const nestedKeys = Object.keys(integration.configuration)
    if (nestedKeys.length > 0) {
      return { ...integration.configuration }
    }
  }

  switch (integration.type) {
    case 'gtm':
      return {
        gtmId: integration.gtmId || null,
        gtmDomain: integration.gtmDomain || null,
      }
    case 'recaptcha':
      return {
        siteKey: integration.siteKey || null,
        secretKey: integration.secretKey || null,
        threshold: integration.threshold || null,
      }
    case 'vwo':
      return {
        smartcode: integration.smartcode || null,
      }
    case 'google_drive':
      return {
        clientId: integration.clientId || null,
        clientSecret: integration.clientSecret || null,
        accessToken: integration.accessToken || null,
        refreshToken: integration.refreshToken || null,
        tokenExpiresIn: integration.tokenExpiresIn || null,
        tokenScope: integration.tokenScope || null,
        authenticatedAt: integration.authenticatedAt || null,
        folderId: integration.folderId || null,
      }
    case 'postcodenl':
      return {
        key: integration.key || null,
        secret: integration.secret || null,
      }
    case 'onedrive':
      return {
        clientId: integration.clientId || null,
        clientSecret: integration.clientSecret || null,
        tenantId: integration.tenantId || null,
        accessToken: integration.accessToken || null,
        refreshToken: integration.refreshToken || null,
        tokenExpiresIn: integration.tokenExpiresIn || null,
        tokenScope: integration.tokenScope || null,
        authenticatedAt: integration.authenticatedAt || null,
        driveId: integration.driveId || null,
      }
    case 'microsoft-dynamics':
    case 'salesforce':
    case 'salesforce-marketing-cloud':
    case 'findock':
      return {
        production: integration.production || {},
        sandbox: integration.sandbox || {},
      }
    default:
      return {}
  }
}

/**
 * Build a Firestore patch that keeps legacy root fields in sync when saving.
 *
 * @param {Object} existing Existing tenant integration data
 * @param {Object} configuration New configuration payload from manage UI
 * @returns {Object}
 */
export const buildConfigurationUpdatePatch = (existing = {}, configuration = {}) => {
  const sharedIntegrationId = existing.sharedIntegrationId
    || LEGACY_TYPE_TO_SHARED[existing.type]
    || existing.type

  const patch = {
    configuration,
    sharedIntegrationId,
    status: 'active',
  }

  const type = existing.type || SHARED_TO_LEGACY_TYPE[sharedIntegrationId]

  switch (type) {
    case 'gtm':
      patch.gtmId = configuration.gtmId ?? null
      patch.gtmDomain = configuration.gtmDomain ?? null
      break
    case 'recaptcha':
      patch.siteKey = configuration.siteKey ?? null
      patch.secretKey = configuration.secretKey ?? null
      patch.threshold = configuration.threshold ?? null
      break
    case 'vwo':
      patch.smartcode = configuration.smartcode ?? null
      break
    case 'google_drive':
      patch.clientId = configuration.clientId ?? null
      patch.clientSecret = configuration.clientSecret ?? null
      patch.accessToken = configuration.accessToken ?? null
      patch.refreshToken = configuration.refreshToken ?? null
      patch.tokenExpiresIn = configuration.tokenExpiresIn ?? null
      patch.tokenScope = configuration.tokenScope ?? null
      patch.authenticatedAt = configuration.authenticatedAt ?? null
      patch.folderId = configuration.folderId ?? null
      patch.type = 'google_drive'
      break
    case 'postcodenl':
      patch.key = configuration.key ?? null
      patch.secret = configuration.secret ?? null
      patch.type = 'postcodenl'
      break
    case 'onedrive':
      patch.clientId = configuration.clientId ?? null
      patch.clientSecret = configuration.clientSecret ?? null
      patch.tenantId = configuration.tenantId ?? null
      patch.accessToken = configuration.accessToken ?? null
      patch.refreshToken = configuration.refreshToken ?? null
      patch.tokenExpiresIn = configuration.tokenExpiresIn ?? null
      patch.tokenScope = configuration.tokenScope ?? null
      patch.authenticatedAt = configuration.authenticatedAt ?? null
      patch.driveId = configuration.driveId ?? null
      patch.type = 'onedrive'
      break
    case 'microsoft-dynamics':
    case 'salesforce':
    case 'salesforce-marketing-cloud':
    case 'findock':
      patch.production = configuration.production || {}
      patch.sandbox = configuration.sandbox || {}
      break
    default:
      break
  }

  return patch
}
